import { createAsyncThunk } from '@reduxjs/toolkit';
import { requestFileSizesByFilePermissions } from 'utils/helpers';

/** Maximum number of file permission ids sent to the size endpoint per request. */
export const FILE_SIZE_CHUNK_SIZE = 50;

/**
 * A rejected chunk longer than this is split in two halves and each half is
 * retried once; a rejected chunk of this length or shorter resolves to null
 * sizes directly.
 */
export const FILE_SIZE_BISECT_MIN = 8;

/**
 * Splits ids into consecutive chunks of at most `size` ids.
 * @param {string[]} ids
 * @param {number} size
 * @returns {string[][]}
 */
const chunkIds = (ids, size) => {
  const chunks = [];
  for (let index = 0; index < ids.length; index += size) {
    chunks.push(ids.slice(index, index + size));
  }
  return chunks;
};

/**
 * Maps every id to null (unresolvable size).
 * @param {string[]} ids
 * @returns {Object<string, null>}
 */
const nullSizesById = (ids) =>
  ids.reduce((sizesById, id) => {
    sizesById[id] = null;
    return sizesById;
  }, {});

/**
 * Maps the size endpoint response (an array of `{ size, filePermissionId, key }`
 * in completion order, entries with a falsy ContentLength omitted) onto every
 * requested id. Ids absent from the response resolve to 0.
 * @param {string[]} ids - the ids that were requested
 * @param {Array<{ size: number, filePermissionId: string }>} response
 * @returns {Object<string, number>}
 */
const mapChunkResponse = (ids, response) => {
  const requestedIds = new Set(ids);
  const sizesById = {};

  ids.forEach((id) => {
    sizesById[id] = 0;
  });

  (Array.isArray(response) ? response : []).forEach((entry) => {
    const id = entry?.filePermissionId;
    if (!id || !requestedIds.has(id)) return;
    const size = Number(entry.size);
    sizesById[id] = Number.isFinite(size) ? size : 0;
  });

  return sizesById;
};

/**
 * Requests the sizes of one chunk and maps the response onto every id.
 * @param {string[]} ids
 * @returns {Promise<Object<string, number>>}
 */
const requestChunkSizes = async (ids) => {
  const response = await requestFileSizesByFilePermissions(ids);
  return mapChunkResponse(ids, response);
};

/**
 * Retries a rejected chunk once by splitting it in two halves (the endpoint
 * rejects a whole batch when one S3 object is missing). Halves are not split
 * again: a rejected half resolves to null sizes. Chunks of
 * FILE_SIZE_BISECT_MIN ids or fewer resolve to null sizes without a retry.
 * Never rejects.
 * @param {string[]} ids - the ids of the rejected chunk
 * @returns {Promise<Object<string, number|null>>}
 */
const retryChunkBisected = async (ids) => {
  if (ids.length <= FILE_SIZE_BISECT_MIN) {
    return nullSizesById(ids);
  }

  const middle = Math.ceil(ids.length / 2);
  const halves = [ids.slice(0, middle), ids.slice(middle)];

  const settledHalves = await Promise.allSettled(
    halves.map((half) => requestChunkSizes(half))
  );

  return settledHalves.reduce((sizesById, result, index) => {
    Object.assign(
      sizesById,
      result.status === 'fulfilled'
        ? result.value
        : nullSizesById(halves[index])
    );
    return sizesById;
  }, {});
};

/**
 * Loads the byte sizes of public project bank files by file permission id.
 * Ids are requested in chunks of FILE_SIZE_CHUNK_SIZE with Promise.allSettled;
 * a rejected chunk is bisected and retried once. The payload
 * `{ sizesById }` covers every requested id: a number (0 when the endpoint
 * omitted the id) or null when the size could not be resolved.
 */
export const loadProjectBankFileSizes = createAsyncThunk(
  'projectBank/loadProjectBankFileSizes',
  async (ids) => {
    const uniqueIds = Array.from(new Set(ids));
    const chunks = chunkIds(uniqueIds, FILE_SIZE_CHUNK_SIZE);

    const settledChunks = await Promise.allSettled(
      chunks.map((chunk) => requestChunkSizes(chunk))
    );

    const chunkResults = await Promise.all(
      settledChunks.map((result, index) =>
        result.status === 'fulfilled'
          ? result.value
          : retryChunkBisected(chunks[index])
      )
    );

    const sizesById = chunkResults.reduce(
      (accumulator, chunkSizesById) =>
        Object.assign(accumulator, chunkSizesById),
      {}
    );

    return { sizesById };
  },
  {
    condition: (ids) => Array.isArray(ids) && ids.length > 0,
  }
);
